import { type CommTemplateChannel, CommTemplateRelatedTo, type CommTemplateStatus } from '../../prisma/enums';

/** Body content type for audit/history log entries (`EntityHistory.contentType`, FK to the `ContentType` lookup table). */
export enum ContentTypeEnum {
    html = 'HTML',
    text = 'TEXT',
}

export const COMM_LOG_DIRECTIONS = {
    INBOUND: 'INBOUND',
    OUTBOUND: 'OUTBOUND',
} as const;

export type CommLogDirection = (typeof COMM_LOG_DIRECTIONS)[keyof typeof COMM_LOG_DIRECTIONS];

export const COMM_CHANNELS = {
    EMAIL: 'EMAIL',
    PHONE: 'PHONE',
    SMS: 'SMS',
} as const;

export type CommLogChannel = (typeof COMM_CHANNELS)[keyof typeof COMM_CHANNELS];

// Alias for backward compatibility
export type CommChannel = CommLogChannel;

export const COMM_TEMPLATE_RELATED_TO = {
    claim: 'claim',
    complaint: 'complaint',
    incident: 'incident',
    org: 'org',
    user: 'user',
    risk: 'risk',
    task: 'task',
    control: 'control',
    mspr: 'mspr',
    lodgement: 'lodgement',
} as const satisfies Record<CommTemplateRelatedTo, CommTemplateRelatedTo>;

export type { CommTemplateRelatedTo };

export const COMM_TEMPLATE_RELATED_TO_LABELS = {
    claim: 'Claim',
    complaint: 'Complaint',
    incident: 'Incident',
    org: 'Org',
    user: 'User',
    risk: 'Risk',
    task: 'Task',
    control: 'Control',
    mspr: 'Material Service Provider',
    lodgement: 'Lodgement',
} as const satisfies Record<CommTemplateRelatedTo, string>;

export const COMM_TEMPLATE_CHANNELS = {
    EMAIL: 'EMAIL',
    SMS: 'SMS',
} as const satisfies Record<CommTemplateChannel, CommTemplateChannel>;

export type { CommTemplateChannel };

export const COMM_TEMPLATE_CHANNEL_LABELS = {
    EMAIL: 'Email',
    SMS: 'SMS',
} as const satisfies Record<CommTemplateChannel, string>;

// Options for Select components
export const COMM_TEMPLATE_RELATED_TO_OPTIONS = Object.values(CommTemplateRelatedTo);

export const COMM_TEMPLATE_STATUSES = {
    DRAFT: 'DRAFT',
    PUBLISHED: 'PUBLISHED',
    ARCHIVED: 'ARCHIVED',
} as const satisfies Record<CommTemplateStatus, CommTemplateStatus>;

export type { CommTemplateStatus };

/** PostHog feature flag gating the claim email routing feature (off by default everywhere). */
export const ENABLE_CLAIM_EMAIL_ROUTING_FLAG = 'enable-claim-email-routing';

/** PostHog feature flag gating the email workload manager inbox. */
export const ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG = 'enable-email-workload-manager';

/**
 * CUR-771: PostHog feature flag (off by default everywhere) that splits the workload manager
 * Inbox into Gmail-style mailboxes — `Inbox` (threads with a received message, anchored on the
 * latest received one) and `Sent` (threads with a sent message, anchored on the latest sent one).
 * Off, the Inbox keeps today's any-direction behaviour and the Sent segment is not offered.
 */
export const SHOW_WM_SENT_MAILBOX_FLAG = 'show-wm-sent-mailbox';

/**
 * Workload manager email mailboxes (CUR-771). `INBOX` and `SENT` follow Gmail semantics — a
 * conversation with both received and sent mail appears in both; `DRAFTS` is the caller's own
 * unsent drafts.
 */
export const WM_MAILBOXES = ['INBOX', 'SENT', 'DRAFTS'] as const;
export type WmMailbox = (typeof WM_MAILBOXES)[number];

/** CUR-449: max session-viewed thread ids the client may pin into the unread-only WM list. */
export const WM_VIEWED_THREADS_CAP = 200;

/** Cost and payload bounds shared by SMS CSV intake and classification. */
export const SMS_CLASSIFICATION_BODY_MAX_CHARS = 15_000;
export const SMS_CLASSIFICATION_PHONE_MAX_CHARS = 64;

/** How long a Twilio `sent` status may await a carrier delivery receipt before tracking resolves. */
export const SMS_SENT_NO_RECEIPT_TIMEOUT_MS = 5 * 60 * 1_000;
